// Keeping a phone or a tablet's screen on while someone is in the world.
//
// Phones dim and lock after half a minute to a couple of minutes without
// a touch, which comes round soon enough while watching a stream, sitting
// at an event, talking or reading chat. On an iPhone a locked screen also
// suspends the page and drops the connection to the region. So while
// someone is in the world on a touch screen the page holds a screen wake
// lock. The browser lets go of it by itself whenever the page is hidden,
// so it is asked for again each time the page comes back into view. A
// desktop is left to sleep as it always has, and so is a laptop with a
// touchscreen, which is plugged in or put away like any other laptop.

import { device, onDeviceChange } from "@/input/device";

/** The parts of a WakeLockSentinel used here. */
export interface WakeSentinel {
  readonly released: boolean;
  release(): Promise<void>;
}

/** The parts of navigator.wakeLock used here. */
export interface WakeLockApi {
  request(type: "screen"): Promise<WakeSentinel>;
}

/**
 * Holds the screen on while it is wanted, the page is in view and the
 * screen is a touch screen, and lets go otherwise. `update` is called
 * whenever one of those may have changed.
 */
export class ScreenWake {
  private wanted = false;
  private held: WakeSentinel | null = null;
  /** A request on its way, so a second is not sent while it is. */
  private asking: Promise<void> | null = null;

  constructor(
    private api: WakeLockApi | undefined,
    private visible: () => boolean,
    private touch: () => boolean,
  ) {}

  /** Whether the lock is held now. */
  get holding(): boolean {
    return !!this.held && !this.held.released;
  }

  /** Want the screen kept on, or not any more. */
  want(on: boolean): void {
    this.wanted = on;
    this.update();
  }

  /** Ask for the lock or give it back, as things now stand. */
  update(): void {
    const should = this.wanted && this.visible() && this.touch();
    if (!should) {
      this.letGo();
      return;
    }
    if (this.holding || this.asking || !this.api) return;
    this.asking = this.api
      .request("screen")
      .then((sentinel) => {
        this.held = sentinel;
        // Given only after it stopped being wanted, or the page went away
        // while it was asked for: given straight back.
        if (!(this.wanted && this.visible() && this.touch())) this.letGo();
      })
      .catch(() => {
        // Refused, by a battery saver or a page not allowed one: the screen
        // sleeps as it would have anyway.
      })
      .finally(() => {
        this.asking = null;
      });
  }

  private letGo(): void {
    const held = this.held;
    this.held = null;
    if (held && !held.released) void held.release().catch(() => undefined);
  }
}

let wake: ScreenWake | null = null;

/**
 * Keep the screen on from now until this is called with false, at logout.
 * Only on a touch screen, and only while the page is in view.
 */
export function keepScreenOn(on: boolean): void {
  if (typeof document === "undefined") return;
  if (!wake) {
    const api = (navigator as Navigator & { wakeLock?: WakeLockApi }).wakeLock;
    const current = new ScreenWake(api, () => document.visibilityState === "visible", () => device().touch && !device().hybrid);
    document.addEventListener("visibilitychange", () => current.update());
    onDeviceChange(() => current.update());
    wake = current;
  }
  wake.want(on);
}
